<template>
  <section class="w-full">
    <form @submit.prevent="
      Object.keys(props.idiomToUpdate).length > 0
        ? updateIdiom()
        : addNewIdiom()
      " class="grid grid-cols-12 gap-4 px-4 mt-3 text-base font-normal">
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('idiom', 'ucfirst') }}</label>
        <CustomDropdown name="lalangue_id" :options="idiomsList" optionLabel="llangue_name" optionValue="llangue_id"
          :loading="fetchingIdioms" filter class="w-full px-3 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('proficiency level', 'ucfirst') }}</label>
        <CustomDropdown name="proficiency_level" :options="proficiencyList" optionLabel="title" optionValue="id"
          filter class="w-full px-3 mt-2.5 rounded-full border border-iron" />
      </span> 
      <span class="col-span-12">
        <Button v-if="Object.keys(props.idiomToUpdate).length > 0" @click="showConfirmDialog = true" type="button"
          :label="translate('delete', 'ucfirst')" 
          class="text-white px-5 py-2 float-left rounded-full bg-red-600" />
        
        <Button type="submit" :label="Object.keys(props.idiomToUpdate).length > 0
            ? translate('update', 'ucfirst')
            : translate('save', 'ucfirst')
          " class="text-white px-5 py-2 float-right rounded-full bg-sea-serpent" />
      </span>
    </form>

    <CustomConfirmDialog :show-confirm="showConfirmDialog" @confirmResponse="confirmDialogResponse" />
    <LoadingDialog :visible="isLoading" />
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeMount } from "vue";
import CustomDropdown from "@/components/Base/CustomDropdown.vue";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { showToast } from "@/composables/toast";
import { translate } from "@/composables/translate";
import requestHandler from "@/composables/requestHandler";
import useLanguageProficiency from "@/composables/LanguageProficiency";
import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
import CustomConfirmDialog from "@/components/Utils/CustomConfirmDialog.vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, number } from "yup";

let toast = useToast();
let isLoading = ref(false);
let showConfirmDialog = ref(false);
let fetchingIdioms = ref(false)
let idiomsList = ref([])
let { proficiencyList } = useLanguageProficiency()

const props = defineProps({
  idiomToUpdate: {
    type: Object,
    required: false,
    default: () => { }
  },
  curriculumId: {
    type: Number,
    required: false
  },
});

const emit = defineEmits(["closeModal", 'idiomsUpdated']);

// ############ Validação formulário ############ 2

const { handleSubmit, setValues } = useForm({
  validationSchema: toTypedSchema(
    object().shape({
      lalangue_id: number().required(translate('this field is required', 'ucfirst')),
      proficiency_level: number().required(translate('this field is required', 'ucfirst'))
    })
  ),
});

// ############ Criação de Métodos/Funções ############

const addNewIdiom = handleSubmit(async (values) => {

  const newValues = {
    lalangue_id: values.lalangue_id,
    laspeaking_level: values.proficiency_level,
    lalistening_level: values.proficiency_level,
    lawriting_level: values.proficiency_level,
    lareading_level: values.proficiency_level,
  };

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest("/api/curriculum/language/create", newValues, "post");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast,
        translate("there was an error adding your idiom", "ucfirst"), "error");
      return;
    }

    showToast(toast, translate("added idiom", "ucfirst"));
    emit("idiomsUpdated", true);
    emit("closeModal", false);
  }
});

let updateIdiom = handleSubmit(async (values) => {

  const newValues = {
    lang_id: props.idiomToUpdate.id || props.idiomToUpdate.lang_id, // garantir que o identificador do registro seja enviado
    lalangue_id: values.lalangue_id,
    laspeaking_level: values.proficiency_level,
    lalistening_level: values.proficiency_level,
    lawriting_level: values.proficiency_level,
    lareading_level: values.proficiency_level,
  };

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest(`/api/curriculum/language/update`, newValues, "put");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(
        toast,
        translate("there was an error updating your idiom", "ucfirst"),
        "warn"
      );
      return;
    }
    showToast(toast, translate("your idiom was updated", "ucfirst"));
    emit("idiomsUpdated", true);
    emit("closeModal", false);
  }
});

let deleteIdiom = handleSubmit(async (values) => {

  const deleteBody = {
    item_type: "language",
    item_id: props.idiomToUpdate.id,
    curriculum_id: props.curriculumId
  };

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest("/api/curriculum/item/delete", deleteBody, "delete");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast, translate("there was an error deleting your idiom", "ucfirst"), "warn");
      return;
    }
    showToast(toast, translate("your idiom was deleted", "ucfirst"));
    emit("closeModal", false);
    emit("idiomsUpdated", true);
  }
});

function confirmDialogResponse(event) {

  showConfirmDialog.value = event.close;
  if (event.accepted) {
    deleteIdiom()
  }
}

async function getListIdioms() {

  fetchingIdioms.value = !fetchingIdioms.value
  const response = await requestHandler.executeRequest("/api/languages/index", {}, "get");

  if (response.success) {
    idiomsList.value = response.data

  }

  fetchingIdioms.value = !fetchingIdioms.value
}


onBeforeMount(() => {
  getListIdioms()
});

onMounted(() => {
  if (Object.keys(props.idiomToUpdate).length > 0) {
    setValues({
      lalangue_id: props.idiomToUpdate.language,
      proficiency_level: props.idiomToUpdate.speaking
    });
  }
})
</script>
